import { AlertTriangle, Loader2, RotateCw } from 'lucide-react';
import type { ReactNode, RefObject } from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import {
  frameStatusNotice,
  isFrameBlank,
  type FrameStatus,
} from './frame-status';

export interface PrototypeFrameProps {
  /** Absolute URL on the sandbox origin, trailing slash included. */
  readonly src: string;
  /** Bumped to force a reload of the same URL. */
  readonly reloadToken: number;
  /** Frame width in CSS pixels, or `null` to fill the canvas. */
  readonly width: number | null;
  /** Announced to assistive technology, e.g. "Checkout redesign, version 3". */
  readonly title: string;
  readonly frameRef: RefObject<HTMLIFrameElement | null>;
  readonly status: FrameStatus;
  readonly onLoad: () => void;
  readonly onRetry: () => void;
  /**
   * Rendered in the frame's own positioning box, over the iframe — the pin
   * popovers and hit targets. See the note on the overlay box below for why it
   * is a slot here rather than something the caller places itself.
   */
  readonly overlay?: ReactNode;
}

/**
 * The iframe the prototype runs in — the one component in Atlas that has to be
 * exactly right.
 *
 * ============================================================================
 * THE SANDBOX
 * ============================================================================
 * `allow-scripts allow-forms allow-popups`, and `allow-same-origin` is never
 * added. Uploaded prototypes are arbitrary third-party JavaScript. Without
 * `allow-same-origin` the framed document runs in an opaque origin: it cannot
 * read Atlas's cookies, storage, or DOM, and it cannot reach back through
 * `window.parent` for anything except `postMessage`. Granting
 * `allow-same-origin` alongside `allow-scripts` would let the framed document
 * remove its own sandbox attribute and escape entirely, which is why the two
 * are never combined. The `sandbox` string below is the security boundary of
 * this whole product; changing it is not a styling decision.
 *
 * ============================================================================
 * WIDTH CHANGES MUST NOT REMOUNT
 * ============================================================================
 * The frame is sized through inline `style.width` and is deliberately absent
 * from `key`. A prototype under review is stateful — a filled-in form, an open
 * accordion, a menu three levels deep — and remounting the element reloads the
 * document and throws all of it away. Switching from Desktop to Mobile must
 * show the reviewer the *same page* at a different width, so `key` tracks only
 * what genuinely requires a new document: the URL, and an explicit retry.
 *
 * ============================================================================
 * FAILURE
 * ============================================================================
 * There is no `error` event to listen to across an opaque origin, so failure is
 * inferred by `frame-status.ts` and surfaced here. A blank frame gets an
 * explanation and a retry button over the top of it; a frame that rendered but
 * whose agent never answered gets a quiet strip along the bottom, because the
 * prototype itself is usable and covering it would be a lie about how bad the
 * problem is.
 *
 * ============================================================================
 * THE OVERLAY BOX
 * ============================================================================
 * There are two nested boxes around the iframe rather than one, and the split
 * is what lets a pin popover stay glued to a pin for free.
 *
 * The inner box clips: it owns the border, the radius and `overflow-hidden`, so
 * the framed document is cut to the rounded corners. The outer box only
 * positions: same geometry, no clipping, and it is what `overlay` is rendered
 * into. A popover placed in the *clipping* box would be sliced off the moment
 * it reached the frame's edge — which is exactly where a pin near the edge puts
 * it — and portalling it to the body instead would mean tracking the frame's
 * screen position through canvas scrolling, the width transition and every
 * window resize.
 *
 * Because the overlay is a sibling of the clipping box rather than a
 * descendant, its coordinate origin is the frame's border box; the iframe's
 * viewport starts one border-width inside that, which `pin-popover-position.ts`
 * accounts for with `FRAME_BORDER_PX`.
 */
export const PrototypeFrame = ({
  src,
  reloadToken,
  width,
  title,
  frameRef,
  status,
  onLoad,
  onRetry,
  overlay,
}: PrototypeFrameProps) => {
  const blank = isFrameBlank(status);
  const notice = frameStatusNotice(status);

  return (
    <div className="relative flex h-full w-full overflow-auto bg-viewer-canvas p-4 sm:p-6 lg:p-8">
      <div
        className={cn(
          /*
           * Centred with `mx-auto`, not `justify-center` on the parent. When the
           * frame is wider than the window — Desktop on a laptop — a centred
           * flex container clips its own overflow on the leading edge and the
           * left of the prototype becomes unreachable. Auto margins collapse to
           * zero instead, and the canvas simply scrolls.
           */
          'relative mx-auto shrink-0 transition-[width] duration-200 ease-out',
          width === null && 'w-full',
        )}
        style={width === null ? undefined : { width: `${String(width)}px` }}
      >
        <div className="relative flex h-full w-full flex-col overflow-hidden rounded-lg border border-viewer-chrome-border bg-white shadow-sm">
          <iframe
            /*
             * The URL and an explicit retry are the only reasons to hand React a
             * new element. Width is not in here on purpose — see the note above.
             */
            key={`${src}#${String(reloadToken)}`}
            ref={frameRef}
            src={src}
            title={title}
            onLoad={onLoad}
            /*
             * NEVER add `allow-same-origin`. See the block comment above.
             */
            sandbox="allow-scripts allow-forms allow-popups"
            className="h-full w-full border-0 bg-white"
          />

          {status === 'loading' ? (
            <div
              className="pointer-events-none absolute inset-0 flex items-center justify-center bg-viewer-canvas"
              aria-live="polite"
            >
              <span className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 aria-hidden="true" className="size-4 animate-spin" />
                Loading the prototype…
              </span>
            </div>
          ) : null}

          {blank && notice !== null ? (
            <div
              role="alert"
              className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-viewer-canvas px-6 text-center"
            >
              <AlertTriangle
                aria-hidden="true"
                className="size-6 text-destructive"
                strokeWidth={1.5}
              />
              <p className="text-base font-medium text-foreground">
                This prototype did not load
              </p>
              <p className="max-w-prose text-sm text-muted-foreground">
                {notice}
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={onRetry}
              >
                <RotateCw aria-hidden="true" />
                Try again
              </Button>
            </div>
          ) : null}
        </div>

        {/*
         * Sibling of the clipping box, not a descendant: a popover for a pin
         * near the frame's edge would otherwise be sliced off by
         * `overflow-hidden`. Its coordinate origin is the frame's border box —
         * `pin-popover-position.ts` offsets by `FRAME_BORDER_PX` to reach the
         * iframe viewport.
         */}
        {overlay}
      </div>

      {!blank && notice !== null ? (
        <div
          role="status"
          className="pointer-events-none absolute inset-x-0 bottom-0 flex justify-center p-4"
        >
          <p className="pointer-events-auto max-w-prose rounded-md border border-viewer-chrome-border bg-viewer-chrome px-3 py-2 text-xs text-viewer-chrome-foreground shadow-sm">
            {notice}
          </p>
        </div>
      ) : null}
    </div>
  );
};
